import React, { useEffect, useMemo, useRef, useState } from "react";
import { t } from "ttag";
import { BaseDialog, Button, Checkbox, DownloadIcon as ImportFileIcon, ExportIcon as ExportFileIcon, IconButton, OpenInNewIcon, RefreshCircleIcon, Row, TextField, Tooltip, Typography, useConfirmation, useToast } from "@coolremote/shared";
import { useStoreActions, useStoreState } from "../../../../models/RootStore";
import {
  useCreateModbusDeviceTemplate,
  useGetSiteModbusDeviceTemplates,
  useUpdateModbusDeviceTemplate,
} from "../../../../hooks/useModbusDeviceTemplatesApi";
import {
  useCreateModbusFunctionalTemplate,
  useGetSiteModbusFunctionalTemplates,
  useUpdateModbusFunctionalTemplate,
} from "../../../../hooks/useModbusFunctionalTemplatesApi";
import {
  useCreateModbusUiTemplate,
  useGetSiteModbusUiTemplates,
  useUpdateModbusUiTemplate,
} from "../../../../hooks/useModbusUiTemplatesApi";
import {
  useCreateModbusDevice,
  useCreateModbusUnit,
  useDeleteModbusUnit,
  useGetDeviceModbusUnits,
  useGetModbusDevice,
  useGetSiteModbusDevices,
  useUpdateModbusDevice,
  useUpdateModbusUnit,
} from "../../../../hooks/useModbusDevicesApi";
import {
  DATA_TYPE_CODE_OPTIONS,
  ENG_UNIT_OPTIONS,
  IRegisterColumn,
  RegisterTable,
  REG_TYPE_NUMERIC_OPTIONS,
  isBitPackableRegisterType,
  UI_TYPE_OPTIONS,
} from "../../RegisterTable";
import styles from "./DeviceMapper.module.css";
import {
  IModbusDeviceRow,
  IModbusDeviceTemplate,
  IModbusDeviceUnit,
  IModbusFunctionalTemplate,
  IModbusUiTemplate,
  ModbusEntityType,
  UI_TYPE_BINARY,
  UI_TYPE_REVERSE_BINARY,
  UI_TYPE_VALUE,
} from "../../types";
import AddFunctionalUnitModal from "../AddFunctionalUnitModal";
import type { IUITemplate } from "../../UITemplateMapper";
import ImportFromLibraryDialog from "../ImportFromLibraryDialog";
import ImportModbusDeviceModal, { IImportedModbusDevice } from "../ImportModbusDeviceModal";
import { downloadModbusDeviceFile } from "../modbusDeviceFileIO";
import ImportDeviceTemplateDialog from "../ImportDeviceTemplateDialog";
import SaveAsTemplateDialog from "../SaveAsTemplateDialog";
import useTypes from "../../../../hooks/useTypes";

const DEFAULT_POLLING_MS = 1000;


interface IDeviceDraft {
  name: string;
  vendorName: string;
  modelName: string;
  device: string;
  slaveId: number | "";
  timeoutMs: number | "";
  host: string;
  port: number | "";
  units: IModbusDeviceUnit[];
}

const emptyDraft = (): IDeviceDraft => ({
  name: "",
  vendorName: "",
  modelName: "",
  device: "",
  slaveId: 1,
  timeoutMs: 200,
  host: "",
  port: "",
  units: [],
});

const interfaceForLine = (line: number): string => (line === 0 ? "modbus.ip" : `modbus.rtu${line}`);

const INTERFACES_CATEGORY_KEY = "lines";

// paramTemplateData is an untyped store — older units may have uiTemplate
// saved as a JSON string rather than a plain object; accept either.
const parseUiTemplate = (raw: unknown): Record<string, any> | undefined => {
  if (!raw) return undefined;
  if (typeof raw !== "string") return raw as Record<string, any>;
  try {
    return JSON.parse(raw);
  } catch {
    return undefined;
  }
};

interface IProps {
  open: boolean;
  deviceId?: string;
  onDone: () => void;
}

const DeviceMapper: React.FC<IProps> = ({ open, deviceId, onDone }) => {
  const { flashError } = useToast();
  const confirm = useConfirmation();
  const types: any = useTypes();
  const isEdit = !!deviceId;
  const siteId = useStoreState((s) => s.selections.selections.siteId) || undefined;
  const modbusLineId = useStoreState((s) => s.selections.selections.modbusLineId);
  const modbusDeviceId = useStoreState((s) => s.selections.selections.modbusDeviceId);
  const allDevices = useStoreState((s) => s.devices.allDevices);
  const selectedCADevice = modbusDeviceId ? allDevices[modbusDeviceId] : null;
  const headerLine = modbusLineId !== null && modbusLineId !== "" ? Number(modbusLineId) : null;
  const getDeviceConfigCategories = useStoreActions((a) => a.devices.getDeviceConfigCategories);
  const getDeviceConfigCategory = useStoreActions((a) => a.devices.getDeviceConfigCategory);
  const [refreshing, setRefreshing] = useState(false);
  const [lineCfg, setLineCfg] = useState<{
    caId: string; lineLabel: string;
    baudRate?: string; parityBits?: string; stopBits?: number | string;
    lineIndex?: number;
  } | null>(null);

  const { data: existingDevice } = useGetModbusDevice(deviceId) as unknown as { data: any };
  const { data: existingUnits } = useGetDeviceModbusUnits(deviceId, siteId) as unknown as { data: any[] | null };
  // Full site device list, used to validate that the device name is unique.
  const { data: siteDevices, refetch: refetchSiteDevices } = useGetSiteModbusDevices(siteId) as unknown as {
    data: any[] | null;
    refetch: () => Promise<any>;
  };

  const [draft, setDraft] = useState<IDeviceDraft>(emptyDraft());
  const [submitted, setSubmitted] = useState(false);
  const [addUnitOpen, setAddUnitOpen] = useState(false);
  const [editUnitIndex, setEditUnitIndex] = useState<number | null>(null);
  const [importLibraryOpen, setImportLibraryOpen] = useState(false);
  const [importDeviceLibraryOpen, setImportDeviceLibraryOpen] = useState(false);
  const [importDeviceFileOpen, setImportDeviceFileOpen] = useState(false);
  const [templateDialogOpen, setTemplateDialogOpen] = useState(false);
  const [savingTemplates, setSavingTemplates] = useState(false);
  const [computedDeviceTplName, setComputedDeviceTplName] = useState("");
  const [computedUnitTplNames, setComputedUnitTplNames] = useState<{ name: string }[]>([]);
  // Which of the above actually changed since load — the Save-to-Library popup
  // only offers these (see openTemplateDialog()).
  const [deviceChanged, setDeviceChanged] = useState(true);
  const [changedUnitIndices, setChangedUnitIndices] = useState<Set<number>>(new Set());
  // Live device-name uniqueness check (debounced while typing).
  const [nameStatus, setNameStatus] = useState<"idle" | "checking" | "ok" | "duplicate">("idle");

  // True if another device on the site already uses `name` (excludes the device
  // being edited so keeping its own name is allowed).
  const isDuplicateName = (name: string) =>
    (siteDevices ?? []).some(
      (d) => d.id !== deviceId && (d.name ?? "").trim().toLowerCase() === name.trim().toLowerCase(),
    );

  // Tracks whether the user edited the draft since it was (re)loaded, so we can
  // warn before discarding unsaved changes on close. Set by the edit handlers,
  // reset when the modal opens and after a successful save.
  const dirtyRef = useRef(false);

  // Snapshot of the device/units as originally loaded from the backend (edit
  // mode only) — set once, alongside the draft-population effects below. Used
  // by openTemplateDialog() to offer only what actually changed for saving to
  // the library, instead of re-offering everything on every save.
  const baselineDeviceRef = useRef<{
    name: string; vendorName: string; modelName: string;
    slaveId: number | ""; timeoutMs: number | ""; host: string; port: number | "";
  } | null>(null);
  const baselineUnitsRef = useRef<Map<string, {
    name: string; entityTemplate?: boolean; entityType?: string; uiTemplate?: Record<string, any>; rows: IModbusDeviceRow[];
  }>>(new Map());

  const onError = (e: any) => flashError(e);

  // Refresh the live line settings from the device config API.
  const handleRefreshLine = async () => {
    const caId = caDeviceObj?.id;
    if (!caId || refreshing || lineInfo.noLine){
       return;
    }
    setRefreshing(true);
    try {
      await fetchLineCfg(caId, lineInfo.lineLabel);
    } finally {
      setRefreshing(false);
    }
  };

  const { mutate: createDevice, isLoading: creating } = useCreateModbusDevice({ onError });
  const { mutate: updateDevice, isLoading: updating } = useUpdateModbusDevice({ onError });
  const { mutate: createUnit, isLoading: creatingUnit } = useCreateModbusUnit({ onError });
  const { mutate: updateUnit, isLoading: updatingUnit } = useUpdateModbusUnit({ onError });
  const { mutate: deleteModbusUnit } = useDeleteModbusUnit({ onError });
  const { mutate: createFunctionalTemplate } = useCreateModbusFunctionalTemplate({ onError });
  const { mutate: updateFunctionalTemplate } = useUpdateModbusFunctionalTemplate({ onError });
  const { mutate: createDeviceTemplate } = useCreateModbusDeviceTemplate({ onError });
  const { mutate: updateDeviceTemplate } = useUpdateModbusDeviceTemplate({ onError });
  const { mutate: createUiTemplate } = useCreateModbusUiTemplate({ onError });
  const { mutate: updateUiTemplate } = useUpdateModbusUiTemplate({ onError });
  // Fetched on demand right before a save (see handleSaveTemplates) rather than
  // kept live, so name-conflict matching always sees the latest library state.
  const { refetch: refetchSiteUiTemplates } = useGetSiteModbusUiTemplates(siteId, false) as unknown as {
    refetch: () => Promise<IModbusUiTemplate[] | undefined>;
  };
  // Fetched on demand from openTemplateDialog() to detect a device/unit whose
  // Library entry was deleted independently
  const { refetch: refetchSiteDeviceTemplates } = useGetSiteModbusDeviceTemplates(siteId, false) as unknown as {
    refetch: () => Promise<IModbusDeviceTemplate[] | undefined>;
  };
  const { refetch: refetchSiteFunctionalTemplates } = useGetSiteModbusFunctionalTemplates(siteId, false) as unknown as {
    refetch: () => Promise<IModbusFunctionalTemplate[] | undefined>;
  };
  const submitting = creating || updating || creatingUnit || updatingUnit;

  useEffect(() => {
    if (open) dirtyRef.current = false;
  }, [open]);

  useEffect(() => {
    if (open){
       refetchSiteDevices();
    }
  }, [open]);

  // Debounced (500ms) device-name uniqueness check that drives the field state.
  useEffect(() => {
    const name = draft.name.trim();
    if (!name) {
      setNameStatus("idle");
      return;
    }
    setNameStatus("checking");
    const handle = setTimeout(() => {
      setNameStatus(isDuplicateName(name) ? "duplicate" : "ok");
    }, 500);
    return () => clearTimeout(handle);
  }, [draft.name, siteDevices, deviceId]);

  useEffect(() => {
    if (open && !isEdit) {
      setDraft({ ...emptyDraft(), device: modbusDeviceId || "" });
      setSubmitted(false);
    }
  }, [open]);

  useEffect(() => {
    if (!isEdit) {
      setDraft((prev) => ({ ...prev, device: modbusDeviceId || "" }));
    }
  }, [isEdit, modbusDeviceId]);

  useEffect(() => {
    if (isEdit && existingDevice) {
      const deviceFields = {
        name: existingDevice.name ?? "",
        vendorName: existingDevice.vendorName ?? "",
        modelName: existingDevice.modelName ?? "",
        slaveId: existingDevice.slaveId ?? ("" as number | ""),
        timeoutMs: existingDevice.timeoutMs ?? ("" as number | ""),
        host: existingDevice.host ?? "",
        port: existingDevice.port ?? ("" as number | ""),
      };
      setDraft((prev) => ({
        ...prev,
        ...deviceFields,
        device: existingDevice.device ?? "",
      }));
      baselineDeviceRef.current = deviceFields;
    }
  }, [isEdit, existingDevice]);

  useEffect(() => {
    if (isEdit && Array.isArray(existingUnits) && existingUnits.length > 0) {
      const units = existingUnits.map((u) => ({
        id: u.id,
        name: u.name ?? "",
        functionalTemplate: u.functionalTemplate ?? "",
        entityTemplate: u.paramTemplateData?.entityTemplate,
        entityType: u.paramTemplateData?.entityType,
        // paramTemplateData is an untyped store — older units may still have
        // uiTemplate saved as a JSON string rather than a plain object.
        uiTemplate: parseUiTemplate(u.paramTemplateData?.uiTemplate),
        rows: u.paramTemplateData?.rows || [],
      }));
      setDraft((prev) => ({ ...prev, units }));
      baselineUnitsRef.current = new Map(
        units.filter((u) => u.id).map((u) => [
          u.id as string,
          { name: u.name, entityTemplate: u.entityTemplate, entityType: u.entityType, uiTemplate: u.uiTemplate, rows: u.rows },
        ]),
      );
    }
  }, [isEdit, existingUnits]);

  const update = <K extends keyof IDeviceDraft>(key: K, value: IDeviceDraft[K]) => {
    dirtyRef.current = true;
    setDraft((prev) => ({ ...prev, [key]: value }));
  };

  const lineMissing = !isEdit && headerLine === null;
  const deviceMissing = !isEdit && !modbusDeviceId;
  const effLine: number | null = isEdit ? existingDevice?.line ?? null : headerLine;
  const effInterface = isEdit
    ? existingDevice?.interface ?? (effLine != null ? interfaceForLine(effLine) : "")
    : headerLine !== null
      ? interfaceForLine(headerLine)
      : "";
  const isTcp = effInterface === "modbus.ip" || effInterface === "modbus.tcp";

  // On edit, resolve the controller from the device's own `device` ref, falling
  // back to the header-selected controller (same device, and reliably loaded in
  // the store) so the line settings still resolve.
  const caDeviceObj = isEdit
    ? ((existingDevice?.device && allDevices[existingDevice.device]) || selectedCADevice)
    : selectedCADevice;
  const lineNgProps = effLine != null && effLine > 0
    ? (caDeviceObj?.hvacLinesExtended?.[String(effLine)]?.ngProps ?? null)
    : null;

  const fetchLineCfg = async (caId: string, targetLineLabel: string) => {
    try {
      const categories: Record<string, string> = await getDeviceConfigCategories({ deviceId: caId });
      if (!(INTERFACES_CATEGORY_KEY in categories)) {
        return;
      }
      const result: any = await getDeviceConfigCategory({ deviceId: caId, category: INTERFACES_CATEGORY_KEY });
      const lines: any[] = result?.configData?.lines ?? [];
      const idx = lines.findIndex((l: any) => l.lineName === targetLineLabel);
      if (idx === -1) {
        return;
      }
      const line = lines[idx];
      setLineCfg({
        caId,
        lineLabel: targetLineLabel,
        baudRate: line.baudRate,
        parityBits: line.parityBits,
        stopBits: line.stopBits,
        lineIndex: idx,
      });
    } catch {
      // Silently ignore — device may be offline or not support this category.
    }
  };

  // Binary register (Bit #) and conversion formula (Convert) require firmware
  // v1.11+ which maps to itcVersion >= 17.  Disable the controls when the edge
  // device reports a lower version.
  const supportsBitConvert = parseInt(caDeviceObj?.itcVersion, 10) >= 17;

  const errors = submitted
    ? {
      name: draft.name.trim() ? undefined : t`Required`,
      vendorName: draft.vendorName.trim() ? undefined : t`Required`,
      modelName: draft.modelName.trim() ? undefined : t`Required`,
      slaveId: draft.slaveId === "" ? t`Required` : (!Number.isInteger(Number(draft.slaveId)) || Number(draft.slaveId) < 0 || Number(draft.slaveId) > 255) ? t`Must be 0–255` : undefined,
      timeoutMs: draft.timeoutMs === "" ? t`Required` : (!Number.isInteger(Number(draft.timeoutMs)) || Number(draft.timeoutMs) <= 0) ? t`Must be a positive integer` : undefined,
      // Only meaningful for TCP — the fields are hidden on RTU lines, and the
      // backend rejects a modbus.tcp device that is missing either one.
      host: isTcp && !draft.host.trim() ? t`Required` : undefined,
      port: !isTcp
        ? undefined
        : draft.port === ""
          ? t`Required`
          : (!Number.isInteger(Number(draft.port)) || Number(draft.port) < 1 || Number(draft.port) > 65535)
            ? t`Must be 1–65535`
            : undefined,
    }
    : null;

  // Duplicate takes priority (shown live while typing); the required error only
  // appears after a save attempt.
  const nameError = nameStatus === "duplicate"
    ? t`A device with this name already exists.`
    : errors?.name;

  const toFunctionalRows = (rows: IModbusDeviceRow[]): IModbusFunctionalTemplate["rows"] =>
    rows.map((r) => ({
      name: r.name,
      readOnly: r.readOnly,
      uiType: r.uiType,
      units: r.units,
      multiplier: r.multiplier,
      minValue: r.minValue,
      maxValue: r.maxValue,
      step: r.step,
      registerType: r.registerType,
      registerAddress: r.registerAddress,
      // Same reasoning as buildUnitPayload below — omit rather than send null.
      ...(r.bitNumber != null ? { bitNumber: r.bitNumber } : {}),
      dataType: r.dataType,
      readFormula: r.readFormula,
      writeFormula: r.writeFormula,
      customEnum: r.customEnum,
      errorPointEnabled: r.errorPointEnabled,
      errorPointValue: r.errorPointValue,
    }));

  // Adding/editing units is now an instant draft operation — no template API calls.
  const handleAddUnit = (
    unitName: string,
    rows: IModbusDeviceRow[],
    entityTemplate: boolean,
    uiTemplate: Record<string, any> | undefined,
    entityType: ModbusEntityType | undefined,
  ) => {
    dirtyRef.current = true;
    setDraft((prev) => ({
      ...prev,
      units: [
        ...prev.units,
        {
          name: unitName,
          rows: JSON.parse(JSON.stringify(rows)),
          ...(entityTemplate ? { entityTemplate: true, entityType } : {}),
          ...(uiTemplate ? { uiTemplate } : {}),
        },
      ],
    }));
    setAddUnitOpen(false);
  };

  const handleEditUnit = (
    unitName: string,
    rows: IModbusDeviceRow[],
    entityTemplate: boolean,
    uiTemplate: Record<string, any> | undefined,
    entityType: ModbusEntityType | undefined,
  ) => {
    if (editUnitIndex == null) return;
    dirtyRef.current = true;
    setDraft((prev) => {
      const units = [...prev.units];
      units[editUnitIndex] = {
        ...units[editUnitIndex],
        name: unitName,
        rows: JSON.parse(JSON.stringify(rows)),
        ...(entityTemplate
          ? { entityTemplate: true, entityType }
          : { entityTemplate: undefined, entityType: undefined }),
        uiTemplate,
      };
      return { ...prev, units };
    });
    setEditUnitIndex(null);
  };

  const handleImportLibrary = (tpl: IModbusFunctionalTemplate) => {
    if (!tpl.id) return;
    dirtyRef.current = true;
    const deviceRows: IModbusDeviceRow[] = (tpl.rows || []).map((r) => ({
      name: r.name,
      readOnly: r.readOnly,
      uiType: r.uiType,
      units: r.units,
      multiplier: r.multiplier ?? 1,
      minValue: r.minValue ?? null,
      maxValue: r.maxValue ?? null,
      step: r.step ?? null,
      customEnum: r.customEnum,
      errorPointEnabled: r.errorPointEnabled ?? false,
      errorPointValue: r.errorPointValue ?? null,
      registerType: 2,
      registerAddress: null,
      dataType: "s16",
    }));
    setDraft((prev) => ({
      ...prev,
      units: [
        ...prev.units,
        {
          name: tpl.name,
          functionalTemplate: tpl.id as string,
          rows: deviceRows,
          // Bring the template's control UI along
          ...(tpl.defaultUiTemplate ? { uiTemplate: tpl.defaultUiTemplate } : {}),
        },
      ],
    }));
    setImportLibraryOpen(false);
  };

  const handleLoadFromDeviceTemplate = async (tpl: IModbusDeviceTemplate) => {
    if (!tpl) return;
    if (draft.units.length > 0) {
      const ok = await confirm({
        title: t`Replace Units?`,
        bodyMessage: t`Loading a device template will replace your current units. Continue?`,
        confirmText: t`Replace`,
        cancelText: t`Cancel`,
      });
      if (!ok) return;
    }
    dirtyRef.current = true;
    setDraft((prev) => ({
      ...prev,
      name: tpl.name ?? prev.name,
      vendorName: tpl.vendorName ?? prev.vendorName,
      modelName: tpl.modelName ?? prev.modelName,
      timeoutMs: tpl.timeoutMs ?? prev.timeoutMs,
      units: (tpl.units || []).map((u: any) => ({
        name: u.name ?? "",
        functionalTemplate: u.functionalTemplate ?? "",
        entityTemplate: u.entityTemplate,
        entityType: u.entityType,
        rows: JSON.parse(JSON.stringify(u.rows || [])),
      })),
    }));
  };

  const buildUnitPayload = (u: IDeviceDraft["units"][number], unitIndex: number) => {
    const rows = (u.rows || []).map((r) => {
      if (r.bitNumber != null) {
        return r;
      }
      const { bitNumber, ...rest } = r;
      return rest;
    });
    const paramTemplateData: Record<string, any> = { unitIndex, rows };
    if (u.entityTemplate) {
      paramTemplateData.entityTemplate = u.entityTemplate;
      paramTemplateData.entityType = u.entityType;
    }
    if (u.uiTemplate) {
      paramTemplateData.uiTemplate = u.uiTemplate;
    }
    return {
      name: (u.name || "").trim() || t`Unit`,
      pollingIntervalMs: DEFAULT_POLLING_MS,
      ...(u.functionalTemplate ? { functionalTemplate: u.functionalTemplate } : {}),
      paramTemplateData,
    };
  };

  // A unit counts as changed if it's new this session (no id yet — covers
  // added and duplicated units) or if any of its saved fields differ from the
  // baseline captured when the device was loaded for editing.
  const isUnitChanged = (unit: IDeviceDraft["units"][number]): boolean => {
    if (!unit.id) return true;
    const base = baselineUnitsRef.current.get(unit.id);
    if (!base) return true;
    return (
      (unit.name || "") !== (base.name || "") ||
      !!unit.entityTemplate !== !!base.entityTemplate ||
      unit.entityType !== base.entityType ||
      JSON.stringify(unit.uiTemplate ?? null) !== JSON.stringify(base.uiTemplate ?? null) ||
      JSON.stringify(unit.rows || []) !== JSON.stringify(base.rows || [])
    );
  };

  // A brand-new device is always "changed"; an edited one is changed if its
  // own fields differ, or if any unit was added, edited, or removed — the
  // device template embeds a full snapshot of every unit, so a unit-only edit
  // still changes what the device template would save.
  const isDeviceChanged = (): boolean => {
    if (!isEdit) return true;
    const base = baselineDeviceRef.current;
    if (!base) return true;
    const fieldsChanged =
      draft.name.trim() !== base.name ||
      draft.vendorName.trim() !== base.vendorName ||
      draft.modelName.trim() !== base.modelName ||
      Number(draft.slaveId) !== Number(base.slaveId) ||
      Number(draft.timeoutMs) !== Number(base.timeoutMs) ||
      draft.host.trim() !== (base.host || "") ||
      String(draft.port ?? "") !== String(base.port ?? "");
    if (fieldsChanged) return true;
    const unitRemoved = [...baselineUnitsRef.current.keys()].some(
      (id) => !draft.units.some((u) => u.id === id),
    );
    return unitRemoved || draft.units.some(isUnitChanged);
  };

  const openTemplateDialog = async () => {
    // Reached only after a successful device save, so the draft is persisted.
    dirtyRef.current = false;

    const deviceHasChanged = isDeviceChanged();
    const changedIndices = new Set(
      draft.units.map((_, i) => i).filter((i) => isUnitChanged(draft.units[i])),
    );

    // Editing without touching anything (device fields untouched, no unit
    // added/edited/removed) leaves nothing to *offer a choice about* — but the
    // Library entries that mirror this device/units may have been deleted from
    // the Library independently of this edit (isDeviceChanged/isUnitChanged
    // only compare the device's own saved state, not whether a Library entry
    // still exists for it). Re-check by name and silently re-add whatever is
    // missing — there's nothing else to ask the user to choose here, since
    // nothing changed — instead of always skipping the popup for good.
    if (isEdit && !deviceHasChanged && changedIndices.size === 0) {
      const [deviceTemplates, functionalTemplates] = await Promise.all([
        refetchSiteDeviceTemplates(),
        refetchSiteFunctionalTemplates(),
      ]);
      const deviceName = draft.name.trim();
      const deviceMissing = !(deviceTemplates ?? []).some((tpl) => tpl.name === deviceName);
      const missingUnitIndices = draft.units
        .map((_, i) => i)
        .filter((i) => !(functionalTemplates ?? []).some((ft) => ft.name === (draft.units[i].name || t`Unit`)));

      if (deviceMissing || missingUnitIndices.length > 0) {
        await handleSaveTemplates(
          deviceMissing,
          null,
          missingUnitIndices,
          draft.units.map(() => null),
          deviceName,
          draft.units.map((u) => ({ name: u.name || t`Unit` })),
        );
        return;
      }

      onDone();
      return;
    }

    setDeviceChanged(deviceHasChanged);
    setChangedUnitIndices(changedIndices);
    // The template name is just the device name (vendor/model are still stored
    // on the template but aren't part of its name). Conflict detection happens
    // inside SaveAsTemplateDialog against these names.
    setComputedDeviceTplName(draft.name.trim());
    setComputedUnitTplNames(draft.units.map((u) => ({ name: u.name || t`Unit` })));
    setTemplateDialogOpen(true);
  };

  const handleExportDevice = async () => {
    try {
      await downloadModbusDeviceFile(
        {
          name: draft.name,
          vendorName: draft.vendorName,
          modelName: draft.modelName,
          slaveId: draft.slaveId,
          timeoutMs: draft.timeoutMs,
          host: draft.host,
          port: draft.port,
        },
        draft.units,
      );
    } catch (err) {
      flashError(err as Error);
    }
  };

  const handleImportDevice = ({ device, units }: IImportedModbusDevice) => {
    dirtyRef.current = true;
    setDraft((prev) => ({
      ...prev,
      name: device.name ?? prev.name,
      vendorName: device.vendorName ?? prev.vendorName,
      modelName: device.modelName ?? prev.modelName,
      slaveId: device.slaveId ?? prev.slaveId,
      timeoutMs: device.timeoutMs ?? prev.timeoutMs,
      host: device.host ?? prev.host,
      port: device.port ?? prev.port,
      units,
    }));
  };

  const handleRequestClose = async () => {
    if (dirtyRef.current) {
      const ok = await confirm({
        title: t`Discard changes?`,
        bodyMessage: t`You have unsaved changes. Are you sure you want to close without saving?`,
        confirmText: t`Discard`,
        cancelText: t`Keep editing`,
      });
      if (!ok) return;
    }
    onDone();
  };

  const handleSave = async () => {
    if (submitting) return;
    setSubmitted(true);
    if (
      !draft.name.trim() || !draft.vendorName.trim() || !draft.modelName.trim() ||
      draft.slaveId === "" || !Number.isInteger(Number(draft.slaveId)) || Number(draft.slaveId) < 0 || Number(draft.slaveId) > 255 ||
      draft.timeoutMs === "" || !Number.isInteger(Number(draft.timeoutMs)) || Number(draft.timeoutMs) <= 0 ||
      // TCP devices must carry host + port; the backend rejects them otherwise.
      (isTcp && (
        !draft.host.trim() ||
        draft.port === "" || !Number.isInteger(Number(draft.port)) ||
        Number(draft.port) < 1 || Number(draft.port) > 65535
      )) ||
      (!isEdit && (!draft.device.trim() || headerLine === null || !modbusDeviceId))
    ) {
      return;
    }
    // Guard against a duplicate name (covers hitting Save before the debounce resolves).
    if (isDuplicateName(draft.name)) {
      setNameStatus("duplicate");
      return;
    }

    const deviceBody: Record<string, any> = {
      name: draft.name.trim(),
      vendorName: draft.vendorName.trim(),
      modelName: draft.modelName.trim(),
      slaveId: Number(draft.slaveId),
      timeoutMs: Number(draft.timeoutMs),
      ...(draft.host.trim() ? { host: draft.host.trim() } : {}),
      ...(draft.port !== "" ? { port: Number(draft.port) } : {}),
    };

    if (isEdit) {
      const wasBound = !!existingDevice?.isEngaged;
      if (wasBound && dirtyRef.current) {
        const ok = await confirm({
          title: t`Device is Bound`,
          bodyMessage: t`Device is currently bound and monitoring specific parameters list. Are you sure you want to make changes to this device parameters?`,
          confirmText: t`Yes, Save Changes`,
          cancelText: t`Cancel`,
        });
        if (!ok) {
          return;
        }
        const unbound = await updateDevice({ modbusDeviceId: deviceId as string, data: { isEngaged: false } });
        if (!unbound) {
          return;
        }
      }

      const updated = await updateDevice({ modbusDeviceId: deviceId as string, data: deviceBody });
      if (!updated) return;
      // Delete backend units that were removed from the draft (kept ids = draft
      // units that still have an id).
      const keptUnitIds = new Set(draft.units.map((u) => u.id).filter(Boolean));
      for (const existingUnit of existingUnits ?? []) {
        if (existingUnit.id && !keptUnitIds.has(existingUnit.id)) {
          await deleteModbusUnit(existingUnit.id);
        }
      }
      for (let i = 0; i < draft.units.length; i++) {
        const u = draft.units[i];
        if (u.id) {
          await updateUnit({ modbusUnitId: u.id, data: buildUnitPayload(u, i) });
        } else {
          await createUnit({ modbusDeviceId: deviceId as string, data: buildUnitPayload(u, i) });
        }
      }

      if (wasBound && dirtyRef.current) {
        await updateDevice({ modbusDeviceId: deviceId as string, data: { isEngaged: true } });
      }

      await openTemplateDialog();
      return;
    }

    const created = await createDevice({
      ...deviceBody,
      device: draft.device.trim(),
      line: headerLine,
      interface: interfaceForLine(headerLine as number),
    });
    if (!created?.id) return;
    for (let i = 0; i < draft.units.length; i++) {
      await createUnit({ modbusDeviceId: created.id, data: buildUnitPayload(draft.units[i], i) });
    }
    await openTemplateDialog();
  };

  const handleSaveTemplates = async (
    saveDevice: boolean,
    replaceDeviceId: string | null,
    unitIndices: number[],
    replaceUnitIds: (string | null)[],
    deviceTplName: string = computedDeviceTplName,
    unitTplNames: { name: string }[] = computedUnitTplNames,
  ) => {
    setSavingTemplates(true);
    if (saveDevice) {
      const deviceTplData = {
        site: siteId,
        name: deviceTplName,
        vendorName: draft.vendorName.trim(),
        modelName: draft.modelName.trim(),
        timeoutMs: Number(draft.timeoutMs),
        units: draft.units.map((u) => ({
          name: u.name,
          ...(u.functionalTemplate ? { functionalTemplate: u.functionalTemplate } : {}),
          ...(u.entityTemplate ? { entityTemplate: u.entityTemplate, entityType: u.entityType } : {}),
          rows: u.rows,
        })),
      } as IModbusDeviceTemplate;
      if (replaceDeviceId) {
        await updateDeviceTemplate({ templateId: replaceDeviceId, data: deviceTplData });
      } else {
        await createDeviceTemplate(deviceTplData);
      }
    }
    // Fetched once up front (rather than per-unit) since library names must be
    // unique within the site, so every unit's Control UI is matched against the
    // same snapshot.
    const existingUiTemplates = unitIndices.some((i) => draft.units[i].uiTemplate)
      ? (await refetchSiteUiTemplates()) ?? []
      : [];
    for (const i of unitIndices) {
      const unit = draft.units[i];
      const unitTplName = unitTplNames[i]?.name || unit.name;
      const unitRows = toFunctionalRows(unit.rows || []);
      const unitTplData = {
        site: siteId,
        name: unitTplName,
        rows: unitRows,
        // Keep the unit's control UI on the library template so the library
        // can indicate (and re-import) it.
        ...(unit.uiTemplate ? { defaultUiTemplate: unit.uiTemplate } : {}),
      } as IModbusFunctionalTemplate;
      if (replaceUnitIds[i]) {
        await updateFunctionalTemplate({ templateId: replaceUnitIds[i]!, data: unitTplData });
      } else {
        await createFunctionalTemplate(unitTplData);
      }

      // Keep the dedicated Control UI library in sync — same name-based
      // conflict rule as everything else in this dialog (see SaveAsTemplateDialog).
      // Rows are carried along too: a Control UI's points reference rows by
      // index, so the library entry must be self-contained to be re-selected
      // later (see ControlUiLibraryDialog).
      const uiTemplate = unit.uiTemplate as IUITemplate | undefined;
      if (uiTemplate) {
        const uiTemplateData = {
          name: uiTemplate.name,
          deviceVendor: draft.vendorName.trim(),
          data: {
            backgroundImage: uiTemplate.backgroundImage,
            points: uiTemplate.points,
            rows: unitRows,
            functionalUnitName: unitTplName,
          },
        } as IModbusUiTemplate;
        const existing = existingUiTemplates.find((t) => t.name === uiTemplate.name);
        if (existing?.id) {
          await updateUiTemplate({ templateId: existing.id, data: uiTemplateData });
        } else {
          await createUiTemplate({ ...uiTemplateData, site: siteId });
        }
      }
    }
    setSavingTemplates(false);
    setTemplateDialogOpen(false);
    onDone();
  };

  // The library only knows about Control UIs already saved to it — units still
  // being drafted on this same device aren't there yet, so a duplicate name
  // between two of them would otherwise slip past validation.
  const siblingUnitUiNames = (excludeIndex?: number): string[] =>
    draft.units
      .filter((_, i) => i !== excludeIndex)
      .map((u) => (u.uiTemplate as IUITemplate | undefined)?.name)
      .filter((n): n is string => !!n);

  const deleteUnit = (unitIndex: number) => {
    dirtyRef.current = true;
    setDraft((prev) => ({
      ...prev,
      units: prev.units.filter((_, i) => i !== unitIndex),
    }));
  };

  const duplicateUnit = (unitIndex: number) => {
    dirtyRef.current = true;
    setDraft((prev) => {
      const source = prev.units[unitIndex];
      const copy = { ...source, rows: JSON.parse(JSON.stringify(source.rows)), id: undefined };
      const units = [...prev.units];
      units.splice(unitIndex + 1, 0, copy);
      return { ...prev, units };
    });
  };

  // Min/Max/Step/Scale only apply to value-type params — mirrors the edit
  // modal's `notValueType`, so the preview blanks the same cells the editor
  // does instead of showing a stale raw number for a binary/enum param.
  const numericOnlyCell = (key: 'minValue' | 'maxValue' | 'step' | 'multiplier') =>
    function NumericOnlyCell({ row }: { row: IModbusDeviceRow }) {
      return row.uiType !== UI_TYPE_VALUE
        ? <span className={styles.muted}>—</span>
        : <span>{row[key] ?? "—"}</span>;
    };

    const isBinaryType = (row: IModbusDeviceRow) => row.uiType === UI_TYPE_BINARY || row.uiType === UI_TYPE_REVERSE_BINARY;

  // Read-only display columns for unit summary
  const baseDisplayColumns: IRegisterColumn<IModbusDeviceRow>[] = [
    { key: "name", label: t`Parameter Name`, width: 160, sticky: true },
    { key: "uiType", label: t`Type`, tooltip: t`The type of parameter that is stored in this register`, width: 120, kind: "select", options: UI_TYPE_OPTIONS },
    { key: "units", label: t`Eng Units`, tooltip: t`Unit of measurement for this parameter's value (e.g. kW, °C)`, width: 90, kind: "select", options: ENG_UNIT_OPTIONS, maxMenuHeight: 260, minMenuWidth: 120 },
    { key: "minValue", label: t`Min`, tooltip: t`Lowest valid value this parameter can report or accept`, width: 80, render: numericOnlyCell("minValue") },
    { key: "maxValue", label: t`Max`, tooltip: t`Highest valid value this parameter can report or accept`, width: 80, render: numericOnlyCell("maxValue") },
    { key: "step", label: t`Step`, tooltip: t`The resolution of change between two adjacent values`, width: 80, render: numericOnlyCell("step") },
    { key: "readOnly", label: t`R/W`, tooltip: t`Register is read-only, write-only, or read/write`, width: 80, kind: "rwBool" },
    { key: "multiplier", label: t`Scale`, tooltip: t`Divider applied to the raw register value`, width: 90, render: numericOnlyCell("multiplier") },
    { key: "registerType", label: t`Reg Type`, tooltip: t`Modbus register category, sets the address range and access rules.`, width: 140, kind: "select", options: REG_TYPE_NUMERIC_OPTIONS },
    { key: "registerAddress", label: t`Register`, tooltip: t`Modbus register address of this parameter.`, width: 90 },
    {
      key: "bitNumber",
      label: t`Bit #`,
      tooltip: t`Bit position (0-15) in the holding register that holds the discrete value`,
      width: 80,
      render: ({ row }) =>
        !supportsBitConvert
          ? <Tooltip title={t`Unavailable on current firmware. Update to v1.11 or later to use this feature.`} placement="top"><span className={styles.muted}>—</span></Tooltip>
          : isBinaryType(row) && isBitPackableRegisterType(row.registerType) && row.bitNumber != null
            ? <span>{row.bitNumber}</span>
            : <span className={styles.muted}>—</span>,
    },
    { key: "dataType", label: t`Data Type`, tooltip: t`Raw bytes interpretation, sets range and precision.`, width: 120, kind: "select", options: DATA_TYPE_CODE_OPTIONS },
        {
      key: "readFormula",
      label: t`Convert`,
      tooltip: (
        <>
          {t`(Optional) Set a logical formula based on the Input value (V), numbers and basic algebric signs`}{" "}
          <b>+, -, *, /, (, )</b>. {t`Example:`} <b>V*2/10</b>
        </>
      ),
      width: 140,
      render: ({ row }) =>
        !supportsBitConvert
          ? <Tooltip title={t`Unavailable on current firmware. Update to v1.11 or later to use this feature.`} placement="top"><span className={styles.muted}>—</span></Tooltip>
          : row.uiType !== UI_TYPE_VALUE || !row.readFormula
            ? <span className={styles.muted}>—</span>
            : <span className={styles.formulaCellText} title={row.readFormula}>{row.readFormula}</span>,
    },
    {
      key: "errorPointEnabled",
      label: t`Error Point`,
      tooltip: t`Map this register to an error. Once checked, set the register value that clears the error.`,
      width: 130,
      render: ({ row }) =>
        row.errorPointEnabled
          ? <span>{row.errorPointValue ?? "—"}</span>
          : <span className={styles.muted}>—</span>,
    },
  ];

  // Line label (e.g. "L3" / "IP/TCP") and interface label ("Modbus RTU" / "Modbus IP")
  // shown as: `<lineLabel>: Line Settings: <interfaceLabel> | ...ngProps`.
  const lineInfo = useMemo(() => {
    // effLine === 0 is a valid line (IP/TCP), so only bail on null/undefined.
    if (effLine == null) {
      return { lineLabel: "", interfaceLabel: "", noLine: true };
    }
    if (effLine === 0 || effInterface === "modbus.ip" || effInterface === "modbus.tcp") {
      return { lineLabel: "IP/TCP", interfaceLabel: "Modbus IP", noLine: false };
    }
    const rtu = effInterface.match(/^modbus\.rtu(\d+)$/);
    if (rtu) {
      return { lineLabel: `L${rtu[1]}`, interfaceLabel: "Modbus RTU", noLine: false };
    }
    return { lineLabel: effInterface, interfaceLabel: "", noLine: false };
  }, [effLine, effInterface]);

  const lineCfgForCurrentLine = lineCfg && caDeviceObj?.id === lineCfg.caId && lineInfo.lineLabel === lineCfg.lineLabel
    ? lineCfg
    : null;

  const columnsForUnit = (unit: IDeviceDraft["units"][number]) => {
    if (!unit.entityTemplate) return baseDisplayColumns;
    // Look up by this unit's OWN entity type, not a fixed one — a power-meter
    // unit's "power"/"energy" property keys mean nothing in controlUnit's map.
    const propsForUnitType: Record<string, any> =
      types?.universalEntityPropertiesByEntityType?.[unit.entityType || "controlUnit"] ?? {};
    const universalPropertyColumn: IRegisterColumn<IModbusDeviceRow> = {
      key: "property",
      label: t`Universal Point`,
      tooltip: t`Check and map this parameter to a specific universal point (e.g. Setpoint for an AC unit, or Energy for a power meter).`,
      width: 180,
      render: ({ row }) => {
        if (!row.property) return <span className={styles.muted}>—</span>;
        const def = propsForUnitType[row.property];
        return <span>{def?.text || row.property}</span>;
      },
    };
    return [...baseDisplayColumns, universalPropertyColumn];
  };


  return (
    <>
      <BaseDialog
        open={open}
        onClose={handleRequestClose}
        onConfirm={handleSave}
        title={isEdit ? t`Edit Modbus Device` : t`New Modbus Device`}
        confirmLabel={isEdit ? t`Save Device` : t`Create Device`}
        cancelLabel={t`Cancel`}
        loading={submitting}
        confirmDisabled={lineMissing || deviceMissing || nameStatus === "duplicate"}
        size="full"
        staticBackdrop
      >
        <div className={styles.deviceMappingStack}>
          {/* Line settings banner + Device Name */}
          <div className={styles.headerBlock}>
            <div className={styles.cardHead}>
              <Typography variant="cardTitle">{t`Modbus Device Configuration`}</Typography>
              <div className={styles.fileActions}>
                <Tooltip title={t`Import device information from xlsx file`} hoverOnly>
                  <IconButton
                    disableRipple
                    className={styles.fileActionIcon}
                    onClick={() => setImportDeviceFileOpen(true)}
                  >
                    <ImportFileIcon width={18} height={18} className={styles.fileActionIconSvg} />
                  </IconButton>
                </Tooltip>
                <Tooltip title={t`Export device information to xlsx file`} hoverOnly>
                  <IconButton disableRipple className={styles.fileActionIcon} onClick={handleExportDevice}>
                    <ExportFileIcon width={18} height={18} className={styles.fileActionIconSvg} />
                  </IconButton>
                </Tooltip>
              </div>
            </div>
            <div className={styles.headerBlockBody}>
              <div className={styles.lineBanner}>
                {lineInfo.noLine ? (
                  <span>
                    <strong>{t`Line Settings:`}{" "}{t`No line selected`}</strong>
                  </span>
                ) : (
                  <>
                    <div className={styles.lineBannerLeft}>
                      <strong>
                        {lineInfo.lineLabel}: {t`Line Settings:`}{lineInfo.interfaceLabel ? ` ${lineInfo.interfaceLabel}` : ""}
                      </strong>
                      {/* Baseline from the store shows immediately; a manual reload
                          (lineCfgForCurrentLine, read live from the device) takes precedence once fetched. */}
                      {(lineCfgForCurrentLine || lineNgProps) && (
                        ` | Baud rate: ${lineCfgForCurrentLine?.baudRate ?? lineNgProps?.baudRate} | Parity: ${lineCfgForCurrentLine?.parityBits ?? lineNgProps?.parityBits} | Stop bits: ${lineCfgForCurrentLine?.stopBits ?? lineNgProps?.stopBits}`
                      )}
                      {caDeviceObj && (
                        <button
                          type="button"
                          className={styles.lineBannerRefreshBtn}
                          onClick={handleRefreshLine}
                          disabled={refreshing}
                          title={t`Reload line settings`}
                          aria-label={t`Reload line settings`}
                        >
                          <RefreshCircleIcon
                            className={`${styles.lineBannerRefreshIcon}${refreshing ? ` ${styles.spinning}` : ""}`}
                          />
                        </button>
                      )}
                    </div>
                    {caDeviceObj && !isTcp && (
                      <Button
                        variant="text"
                        size="small"
                        color="#4263EB"
                        classes={{ root: styles.lineBannerLink }}
                        endIcon={<OpenInNewIcon width={14} height={14} color="#4263EB" />}
                        onClick={() => {
                          // Pass the line's name (e.g. "L1") rather than a
                          const url = !lineInfo.noLine
                            ? `/device-configuration/${caDeviceObj.id}?scrollTo=${encodeURIComponent(lineInfo.lineLabel)}`
                            : `/device-configuration/${caDeviceObj.id}`;
                          window.open(url, "_blank", "noopener,noreferrer");
                        }}
                        aria-label={t`Edit line settings in device configuration`}
                      >
                        {t`Edit in device config`}
                      </Button>
                    )}
                  </>
                )}
              </div>
              <Row justify="space-between" align="center">
                <div className={styles.deviceNameWrap}>
                  <TextField
                    label={t`Device Name`}
                    fullWidth
                    error={nameError}
                    value={draft.name}
                    onChange={(v: string) => update("name", v)}
                    placeholder={t`e.g., Power Meter Top Floor`}
                  />
                </div>
                {!isEdit && (
                  <Button white size="medium" onClick={() => setImportDeviceLibraryOpen(true)}>
                    {t`Device Library`}
                  </Button>
                )}
              </Row>
              <div className={styles.fieldGrid}>
                <Typography variant="sectionLabel" color="primary">{t`General Info`}</Typography>
                <div className={styles.fieldRow}>
                  <TextField
                    label={t`Manufacturer`}
                    fullWidth
                    error={errors?.vendorName}
                    value={draft.vendorName}
                    onChange={(v: string) => update("vendorName", v)}
                    placeholder={t`e.g., Schneider Electric`}
                  />
                  <TextField
                    label={t`Model`}
                    fullWidth
                    error={errors?.modelName}
                    value={draft.modelName}
                    onChange={(v: string) => update("modelName", v)}
                    placeholder={t`e.g., PM5350`}
                  />
                  <TextField
                    label={t`Slave ID`}
                    type="number"
                    fullWidth
                    error={errors?.slaveId}
                    value={draft.slaveId === "" ? "" : String(draft.slaveId)}
                    onChange={(v: string) => update("slaveId", v === "" ? "" : Number(v))}
                  />
                  <TextField
                    label={t`Comm Timeout (ms)`}
                    type="number"
                    fullWidth
                    error={errors?.timeoutMs}
                    value={draft.timeoutMs === "" ? "" : String(draft.timeoutMs)}
                    onChange={(v: string) => update("timeoutMs", v === "" ? "" : Number(v))}
                  />
                </div>
                {isTcp && (
                  <div className={styles.fieldRow}>
                    <TextField
                      label={t`Host`}
                      fullWidth
                      value={draft.host}
                      onChange={(v: string) => update("host", v)}
                      error={errors?.host}
                      placeholder={t`e.g., 192.168.1.100`}
                    />
                    <TextField
                      label={t`Port`}
                      type="number"
                      fullWidth
                      inputClassName={styles.noSpinner}
                      value={draft.port === "" ? "" : String(draft.port)}
                      onChange={(v: string) => update("port", v === "" ? "" : Number(v))}
                      error={errors?.port}
                      placeholder={t`e.g., 502`}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>

          {(isEdit || draft.units?.length > 0) ? (
            /* Functional Unit Mapping — edit mode only */
            <div className={styles.card}>
              <div className={styles.cardHead}>
                <Typography variant="cardTitle">{t`Functional Unit Mapping`}</Typography>
                <Row gap={8}>
                  <Button white size="medium" onClick={() => setImportLibraryOpen(true)}>
                    {t`Functional Unit Library`}
                  </Button>
                  <Button white size="medium" onClick={() => setAddUnitOpen(true)}>
                    + {t`Add Functional Unit`}
                  </Button>
                </Row>
              </div>
              <div className={styles.cardBody}>
                {draft.units.length === 0 ? (
                  <Typography variant="meta">
                    {t`No units yet. Click "+ Add Functional Unit" to add units, or import from the library.`}
                  </Typography>
                ) : (
                  <div className={styles.appUnitStack}>
                    {draft.units.map((unit, unitIndex) => (
                      <div className={styles.appUnitCard} key={unitIndex}>
                        <div className={styles.appUnitHead}>
                          <div className={styles.appUnitName}>
                            <div className={styles.appUnitBadge}>{unitIndex + 1}</div>
                            <div className={styles.unitNameFieldWrap}>
                              <TextField
                                label={t`App Unit Name`}
                                value={unit.name || ""}
                                disabled
                                fullWidth
                              />
                            </div>
                            <div className={styles.unitUiTemplateWrap}>
                              <label className={styles.unitUiTemplateLabel}>{t`Control UI`}</label>
                              <div className={styles.unitUiTemplateField}>
                                <span className={`${styles.unitUiTemplateName} ${unit.uiTemplate ? styles.unitUiTemplateNameSet : ""}`}>
                                  {unit.uiTemplate?.name || t`Not set up yet`}
                                </span>
                              </div>
                            </div>
                            <label className={styles.unitCheckboxLabel}>
                              <Checkbox size="small" checked={!!unit.entityTemplate} disabled />
                              <Typography component="span" variant="body">
                                {t`Map to Universal`} {unit.entityType === "powerMeter" ? t`Power Meter` : t`AC Unit`}
                              </Typography>
                            </label>
                          </div>
                          <Row gap={8}>
                            <Button white size="small" onClick={() => setEditUnitIndex(unitIndex)}>{t`Edit`}</Button>
                            <Button white size="small" onClick={() => duplicateUnit(unitIndex)}>{t`Duplicate`}</Button>
                            <Button white size="small" onClick={() => deleteUnit(unitIndex)}>{t`Remove App Unit`}</Button>
                          </Row>
                        </div>
                        {(unit.rows?.length ?? 0) > 0 && (
                          <div className={styles.tableArea}>
                            <RegisterTable<IModbusDeviceRow>
                              columns={columnsForUnit(unit)}
                              rows={unit.rows || []}
                              onUpdateRow={() => { }}
                              readOnly
                              scrollX
                            />
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ) : (
            <Row justify="flex-end">
              <Button white size="medium" onClick={() => setAddUnitOpen(true)}>
                + {t`Add Functional Unit`}
              </Button>
            </Row>
          )}
        </div>

        {/* Add Functional Unit Modal */}
        <AddFunctionalUnitModal
          open={addUnitOpen}
          onClose={() => setAddUnitOpen(false)}
          onSave={handleAddUnit}
          deviceId={draft.device || undefined}
          slaveId={draft.slaveId}
          iface={effInterface}
          host={draft.host}
          port={draft.port}
          timeoutMs={draft.timeoutMs}
          unitIndex={draft.units.length}
          siteId={siteId}
          siblingUiNames={siblingUnitUiNames()}
          supportsBitConvert={supportsBitConvert}
        />

        {/* Edit Functional Unit Modal */}
        <AddFunctionalUnitModal
          open={editUnitIndex !== null}
          initialName={editUnitIndex != null ? draft.units[editUnitIndex]?.name : ""}
          initialRows={editUnitIndex != null ? draft.units[editUnitIndex]?.rows : undefined}
          initialEntityType={editUnitIndex != null && draft.units[editUnitIndex]?.entityTemplate ? (draft.units[editUnitIndex]?.entityType as ModbusEntityType) || "controlUnit" : ""}
          initialUiTemplate={editUnitIndex != null ? draft.units[editUnitIndex]?.uiTemplate as IUITemplate | undefined : undefined}
          onClose={() => setEditUnitIndex(null)}
          onSave={handleEditUnit}
          deviceId={draft.device || undefined}
          slaveId={draft.slaveId}
          iface={effInterface}
          host={draft.host}
          port={draft.port}
          timeoutMs={draft.timeoutMs}
          unitIndex={editUnitIndex ?? 0}
          siteId={siteId}
          siblingUiNames={editUnitIndex != null ? siblingUnitUiNames(editUnitIndex) : []}
          supportsBitConvert={supportsBitConvert}
        />

      </BaseDialog>

      <ImportDeviceTemplateDialog
        open={importDeviceLibraryOpen}
        siteId={siteId}
        onClose={() => setImportDeviceLibraryOpen(false)}
        onLoad={async (tpl) => { await handleLoadFromDeviceTemplate(tpl); setImportDeviceLibraryOpen(false); }}
      />

      <ImportFromLibraryDialog
        open={importLibraryOpen}
        siteId={siteId}
        loadedTemplateIds={new Set()}
        onClose={() => setImportLibraryOpen(false)}
        onLoad={handleImportLibrary}
      />

      <ImportModbusDeviceModal
        open={importDeviceFileOpen}
        isTcp={isTcp}
        onClose={() => setImportDeviceFileOpen(false)}
        onImport={handleImportDevice}
      />

      {/* Save as Templates — shown after successful device create or edit */}
      <SaveAsTemplateDialog
        open={templateDialogOpen}
        siteId={siteId}
        deviceTemplateName={computedDeviceTplName}
        deviceChanged={deviceChanged}
        units={computedUnitTplNames}
        changedUnitIndices={changedUnitIndices}
        saving={savingTemplates}
        onClose={() => setTemplateDialogOpen(false)}
        onSave={handleSaveTemplates}
      />
    </>
  );
};

export default DeviceMapper;
